import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api } from "@/lib/api";

const inp = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors";
const F = [
  ["name", "Firmenname"], ["owner", "Inhaber"], ["street", "Straße & Nr."], ["zip", "PLZ"], ["city", "Ort"],
  ["email", "E-Mail"], ["phone", "Telefon"], ["bank_holder", "Kontoinhaber"], ["iban", "IBAN"], ["bic", "BIC"],
  ["bank", "Bank"], ["ust_id", "USt-IdNr."], ["tax_number", "Steuernummer"],
];

export default function CompanyView() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/company/full").then((r) => setC(r.data)).catch(() => toast.error("Fehler beim Laden.")); }, []);
  if (!c) return <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>;

  const set = (k) => (e) => setC((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", { ...c, tax_rate: Number(c.tax_rate) || 0, panzerglas_price: Number(c.panzerglas_price) || 0, geraetereinigung_price: Number(c.geraetereinigung_price) || 0 });
      toast.success("Firmendaten gespeichert. Rechnungen & Website sind aktualisiert.");
    } catch (e) { toast.error("Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="company-view">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl">Firmendaten</h1>
        <button onClick={save} disabled={saving} data-testid="save-company" className="inline-flex items-center gap-2 rounded-full bg-white text-[#050505] px-6 py-3 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50"><Save size={16} /> {saving ? "…" : "Speichern"}</button>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7 mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-[#888] mb-3">Steuerstatus</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button type="button" data-testid="mode-kleinunternehmer"
            onClick={() => setC((s) => ({ ...s, kleinunternehmer: true, tax_rate: 0 }))}
            className={`text-left rounded-2xl border p-4 transition-colors ${c.kleinunternehmer ? "border-white bg-white/10" : "border-white/10 hover:border-white/30"}`}>
            <span className="block text-white font-medium">Kleinunternehmer</span>
            <span className="block text-xs text-[#888] mt-1">§ 19 UStG – keine Umsatzsteuer, §-19-Hinweis auf Rechnung & Website.</span>
          </button>
          <button type="button" data-testid="mode-regel"
            onClick={() => setC((s) => ({ ...s, kleinunternehmer: false, tax_rate: Number(s.tax_rate) || 19 }))}
            className={`text-left rounded-2xl border p-4 transition-colors ${!c.kleinunternehmer ? "border-white bg-white/10" : "border-white/10 hover:border-white/30"}`}>
            <span className="block text-white font-medium">Regelbesteuert (Großunternehmen)</span>
            <span className="block text-xs text-[#888] mt-1">Umsatzsteuer wird ausgewiesen – auf Rechnung & Website.</span>
          </button>
        </div>
        {!c.kleinunternehmer && (
          <div className="mt-4 flex items-center gap-3">
            <label className="text-sm text-[#888]">Umsatzsteuer %</label>
            <input data-testid="tax-rate" type="number" value={c.tax_rate} onChange={set("tax_rate")} className="w-28 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none" />
          </div>
        )}
        <p className="text-xs text-[#666] mt-3">Nach dem Speichern gilt die Auswahl sofort für neue Rechnungen, Angebote und die öffentliche Website.</p>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7 mb-6 space-y-4">
        <p className="text-xs font-mono uppercase tracking-widest text-[#888] mb-1">Zusatzleistungen bei der Reparaturbuchung</p>
        <p className="text-xs text-[#666] -mt-2">Kunden können diese optionalen Extras bei einer normalen Reparatur dazubuchen. Preis und Sichtbarkeit legst du hier fest.</p>

        <div className="rounded-2xl border border-white/10 p-4">
          <button type="button" data-testid="toggle-panzerglas-active"
            onClick={() => setC((s) => ({ ...s, panzerglas_active: !s.panzerglas_active }))}
            className={`w-full text-left rounded-xl border p-4 transition-colors ${c.panzerglas_active ? "border-white bg-white/10" : "border-white/10 hover:border-white/30"}`}>
            <span className="block text-white font-medium">{c.panzerglas_active ? "Panzerglas aktiv – im Buchungsformular sichtbar" : "Panzerglas deaktiviert – im Buchungsformular ausgeblendet"}</span>
          </button>
          <div className="mt-3 flex items-center gap-3">
            <label className="text-sm text-[#888]">Preis (€)</label>
            <input data-testid="panzerglas-price" type="number" step="0.01" min="0" value={c.panzerglas_price}
              onChange={(e) => setC((s) => ({ ...s, panzerglas_price: e.target.value }))}
              className="w-28 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none" />
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 p-4">
          <button type="button" data-testid="toggle-geraetereinigung-active"
            onClick={() => setC((s) => ({ ...s, geraetereinigung_active: !s.geraetereinigung_active }))}
            className={`w-full text-left rounded-xl border p-4 transition-colors ${c.geraetereinigung_active ? "border-white bg-white/10" : "border-white/10 hover:border-white/30"}`}>
            <span className="block text-white font-medium">{c.geraetereinigung_active ? "Gerätereinigung aktiv – im Buchungsformular sichtbar" : "Gerätereinigung deaktiviert – im Buchungsformular ausgeblendet"}</span>
            <span className="block text-xs text-[#888] mt-1">Lautsprecher, Hörmuschel, Ladeanschluss.</span>
          </button>
          <div className="mt-3 flex items-center gap-3">
            <label className="text-sm text-[#888]">Preis (€)</label>
            <input data-testid="geraetereinigung-price" type="number" step="0.01" min="0" value={c.geraetereinigung_price}
              onChange={(e) => setC((s) => ({ ...s, geraetereinigung_price: e.target.value }))}
              className="w-28 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none" />
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7 mb-6 space-y-4">
        <p className="text-xs font-mono uppercase tracking-widest text-[#888] mb-1">Adresse auf der Website</p>
        <button type="button" data-testid="toggle-show-address"
          onClick={() => setC((s) => ({ ...s, show_address: !s.show_address }))}
          className={`w-full text-left rounded-2xl border p-4 transition-colors ${c.show_address ? "border-white bg-white/10" : "border-white/10 hover:border-white/30"}`}>
          <span className="block text-white font-medium">{c.show_address ? "Adresse im Footer sichtbar" : "Adresse im Footer unsichtbar"}</span>
          <span className="block text-xs text-[#888] mt-1">Steuert, ob deine Firmenadresse im Footer für alle Besucher sichtbar ist.</span>
        </button>
        <button type="button" data-testid="toggle-show-address-impressum"
          onClick={() => setC((s) => ({ ...s, show_address_impressum: !s.show_address_impressum }))}
          className={`w-full text-left rounded-2xl border p-4 transition-colors ${c.show_address_impressum ? "border-white bg-white/10" : "border-white/10 hover:border-white/30"}`}>
          <span className="block text-white font-medium">{c.show_address_impressum ? "Adresse im Impressum sichtbar" : "Adresse im Impressum unsichtbar"}</span>
          <span className="block text-xs text-[#888] mt-1">Steuert unabhängig vom Footer, ob die Adresse im Impressum angezeigt wird.</span>
        </button>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {F.map(([k, label]) => (
          <div key={k}>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">{label}</label>
            <input data-testid={`co-${k}`} value={c[k] || ""} onChange={set(k)} className={inp} />
          </div>
        ))}
      </div>
    </div>
  );
}
